import { ActivityTypeDto } from '@/api/ApiClient';
import { isHtmlContentEmpty } from '@/lib/utils/textUtils';
import { FieldValidation } from '@/lib/utils/submitValidation';
import type { PublicationTypeDto } from '@/api/ApiClient';

export type RecipeActivitySubmitValidationOptions = {
  /** From the loaded recipe template; preparation only requires instrument when this is true. */
  templateUsesInstrument?: boolean;
};

/** Shared submit validation for sample recipe/proc activities. */
export function createRecipeActivitySubmitValidation(
  activity: {
    instrument?: string;
    runtime?: number;
    technique?: string;
    type?: string;
  },
  options?: RecipeActivitySubmitValidationOptions
): FieldValidation[] {
  const fields: FieldValidation[] = [];

  const isPreparation = activity.type === ActivityTypeDto.Preparation;
  const instrumentRequired = isPreparation ? options?.templateUsesInstrument === true : !!activity.technique;

  if (instrumentRequired) {
    fields.push({
      key: 'instrument',
      label: 'Instrument',
      hasValue: !!activity.instrument && activity.instrument.trim() !== '',
      value: activity.instrument,
    });
  }

  // Runtime is required if instrument is selected, and must be >= 0.01
  if (activity.instrument) {
    fields.push({
      key: 'runtime',
      label: 'Runtime',
      hasValue: activity.runtime !== undefined && activity.runtime !== null,
      value: activity.runtime,
      validator: (val: number) => {
        if (val === undefined || val === null) {
          return 'Runtime is required when an instrument is selected';
        }
        if (val < 0.01) {
          return 'Runtime must be at least 0.01';
        }
        return true;
      },
    });
  }

  return fields;
}

/** Shared submit validation for theory activities. */
export function createTheoryActivitySubmitValidation(activity: {
  software?: string;
  simulationTechnique?: string;
  version?: string;
}): FieldValidation[] {
  const fields: FieldValidation[] = [];

  fields.push(
    {
      key: 'software',
      label: 'Software',
      hasValue: !!activity.software && activity.software.trim() !== '',
      value: activity.software,
    },
    {
      key: 'simulationTechnique',
      label: 'Method',
      hasValue: !!activity.simulationTechnique && activity.simulationTechnique.trim() !== '',
      value: activity.simulationTechnique,
    },
    {
      key: 'version',
      label: 'Version',
      hasValue: !!activity.version && activity.version.trim() !== '',
      value: activity.version,
    }
  );

  return fields;
}

/** Shared submit validation for storage activities. */
export function createStorageActivitySubmitValidation(activity: { sampleLocation?: string }): FieldValidation[] {
  const fields: FieldValidation[] = [];

  fields.push({
    key: 'sampleLocation',
    label: 'Sample Location',
    hasValue: !!activity.sampleLocation && activity.sampleLocation.trim() !== '',
    value: activity.sampleLocation,
  });

  return fields;
}

/** Shared submit validation for project activities. */
export function createProjectActivitySubmitValidation(activity: { type?: string; files?: any[] }): FieldValidation[] {
  const fields: FieldValidation[] = [];

  if (activity.type === ActivityTypeDto.Report || activity.type === ActivityTypeDto.Proposal) {
    fields.push({
      key: 'files',
      label: activity.type === ActivityTypeDto.Report ? 'Report' : 'Proposal',
      hasValue: !!activity.files && activity.files.length > 0,
      value: activity.files,
      validator: (files: any[]) => {
        if (!files || files.length === 0) {
          return `A ${activity.type === ActivityTypeDto.Report ? 'report' : 'proposal'} has to be attached`;
        }
        return true;
      },
    });
  }

  return fields;
}

/** Maps a loaded {@link DataPackageDto}-shaped object into the flat shape expected by {@link createDataPackageSubmitValidation}. */
export function toDataPackageSubmitValidationInput(dataPackage: {
  label?: string;
  description?: string;
  collaborators?: { id?: string }[];
  keywords?: string[];
  synthesisInstruments?: { id?: string }[];
  samples?: unknown[];
}): {
  label?: string;
  description?: string;
  collaboratorIds: string[];
  keywords: string[];
  synthesisInstrumentIds: string[];
  samples: unknown[];
} {
  return {
    label: dataPackage.label,
    description: dataPackage.description,
    collaboratorIds: dataPackage.collaborators?.map((c) => c.id).filter((id): id is string => !!id) || [],
    keywords: dataPackage.keywords || [],
    synthesisInstrumentIds: dataPackage.synthesisInstruments?.map((s) => s.id).filter((id): id is string => !!id) || [],
    samples: dataPackage.samples ?? [],
  };
}

/** Shared submit validation for data packages. */
export function createDataPackageSubmitValidation(dataPackage: {
  label?: string;
  description?: string;
  collaboratorIds?: string[];
  keywords?: string[];
  synthesisInstrumentIds?: string[];
  samples?: any[];
}): FieldValidation[] {
  const fields: FieldValidation[] = [];

  fields.push(
    {
      key: 'label',
      label: 'Title',
      hasValue: !!dataPackage.label && dataPackage.label.trim() !== '',
      value: dataPackage.label,
    },
    {
      key: 'description',
      label: 'Description',
      hasValue: !!dataPackage.description && !isHtmlContentEmpty(dataPackage.description),
      value: dataPackage.description,
      validator: (val: string) => {
        if (!val || isHtmlContentEmpty(val)) {
          return 'Description is required';
        }
        return true;
      },
    },
    {
      key: 'collaboratorIds',
      label: 'Collaborators',
      hasValue: !!dataPackage.collaboratorIds && dataPackage.collaboratorIds.length > 0,
      value: dataPackage.collaboratorIds,
      validator: (val: string[]) => {
        if (!val || val.length === 0) {
          return 'At least one collaborator is required';
        }
        return true;
      },
    },
    {
      key: 'keywords',
      label: 'Keywords',
      hasValue: !!dataPackage.keywords && dataPackage.keywords.length > 0,
      value: dataPackage.keywords,
      validator: (val: string[]) => {
        if (!val || val.length === 0) {
          return 'At least one keyword is required';
        }
        return true;
      },
    }
  );

  if (dataPackage.samples && dataPackage.samples.length > 0) {
    fields.push({
      key: 'synthesisInstrumentIds',
      label: 'Synthesis Instruments',
      hasValue: !!dataPackage.synthesisInstrumentIds && dataPackage.synthesisInstrumentIds.length > 0,
      value: dataPackage.synthesisInstrumentIds,
      validator: (val: string[]) => {
        if (!val || val.length === 0) {
          return 'At least one synthesis instrument is required';
        }
        return true;
      },
    });
  }

  return fields;
}

/** Shared submit validation for shipping. */
export function createShippingSubmitValidation(shipping: {
  localTransfer?: boolean;
  address?: string;
  returnAddress?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  researcherPhone?: string;
  shippingType?: string;
  date?: Date;
  samples?: any[];
}): FieldValidation[] {
  const fields: FieldValidation[] = [];

  if (shipping.localTransfer) {
    fields.push({
      key: 'date',
      label: 'Sample Transfer Date',
      hasValue: !!shipping.date,
      value: shipping.date,
    });
  } else {
    fields.push(
      {
        key: 'address',
        label: 'Address',
        hasValue: !!shipping.address && shipping.address.trim() !== '',
        value: shipping.address,
      },
      {
        key: 'returnAddress',
        label: 'Return Address',
        hasValue: !!shipping.returnAddress && shipping.returnAddress.trim() !== '',
        value: shipping.returnAddress,
        validator: (val: string) => {
          if (!val) return false;
          if (val.indexOf('University Park') === 0 || val.indexOf('???') !== -1) {
            return 'Please complete the return address';
          }
          return true;
        },
      },
      {
        key: 'contactName',
        label: 'Contact Name',
        hasValue: !!shipping.contactName && shipping.contactName.trim() !== '',
        value: shipping.contactName,
      },
      {
        key: 'contactEmail',
        label: 'Contact Email',
        hasValue: !!shipping.contactEmail && shipping.contactEmail.trim() !== '',
        value: shipping.contactEmail,
      },
      {
        key: 'contactPhone',
        label: 'Contact Phone',
        hasValue: !!shipping.contactPhone && shipping.contactPhone.trim() !== '',
        value: shipping.contactPhone,
      },
      {
        key: 'researcherPhone',
        label: 'Researcher Phone',
        hasValue: !!shipping.researcherPhone && shipping.researcherPhone.trim() !== '',
        value: shipping.researcherPhone,
      },
      {
        key: 'shippingType',
        label: 'Shipping Type',
        hasValue: !!shipping.shippingType && shipping.shippingType.trim() !== '',
        value: shipping.shippingType,
      }
    );
  }

  fields.push({
    key: 'samples',
    label: 'Samples',
    hasValue: !!shipping.samples && shipping.samples.length > 0,
    value: shipping.samples,
    validator: (samples: any[]) => {
      if (!samples || samples.length === 0) {
        return 'At least one sample is required';
      }
      return true;
    },
  });

  return fields;
}

function hasPublicationHtmlContent(value?: string): boolean {
  if (!value) return false;
  const stripped = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return stripped.length > 0;
}

/** Shared submit validation for publications (details + create/edit dialog). */
export function createPublicationSubmitValidation(input: {
  title?: string;
  authors?: string;
  journal?: string;
  cite?: string;
  doi?: string;
  type?: PublicationTypeDto | string;
  year?: number;
  requireMainScienceDriver?: boolean;
  selectedScienceDriverIds?: number[];
}): FieldValidation[] {
  const fields: FieldValidation[] = [
    { key: 'title', label: 'Title', hasValue: hasPublicationHtmlContent(input.title), value: input.title },
    { key: 'authors', label: 'Authors', hasValue: hasPublicationHtmlContent(input.authors), value: input.authors },
    { key: 'journal', label: 'Journal', hasValue: Boolean(input.journal?.trim()), value: input.journal },
    { key: 'cite', label: 'Citation', hasValue: hasPublicationHtmlContent(input.cite), value: input.cite },
    { key: 'doi', label: 'DOI', hasValue: Boolean(input.doi?.trim()), value: input.doi },
    { key: 'type', label: 'Type', hasValue: Boolean(input.type), value: input.type },
    {
      key: 'year',
      label: 'Year',
      hasValue: input.year !== undefined && input.year !== null,
      value: input.year,
      validator: (val: number | undefined) => {
        if (val === undefined || val === null) return 'Year is required';
        if (val < 2000 || val > 2199) return 'Year must be between 2000 and 2199';
        return true;
      },
    },
  ];

  if (input.requireMainScienceDriver) {
    fields.push({
      key: 'mainScienceDriver',
      label: 'Main Science Driver',
      hasValue: (input.selectedScienceDriverIds?.length || 0) > 0,
      value: input.selectedScienceDriverIds,
    });
  }

  return fields;
}
